import { useEffect, useState } from 'react';
import { apiClient } from '../../lib/api';
import { AccountListRow, AccountDetail, LinkedInReply, WorkspaceMember } from '../../types/accounts';
import { StandupClientRow } from '../../types/standup';
import { Card, Badge, Spinner } from '../ui';
import { ChannelFunnel } from './ChannelFunnel';
import { ReplyRow } from './ReplyRow';
import { CampaignStatsTable } from './CampaignStatsTable';
import { ProjectMetaBar } from './ProjectMetaBar';
import { ClientBriefPanel } from '../common/ClientBriefPanel';
import { ClientTimeline } from '../common/ClientTimeline';
import { EntityCampaignHistory } from '../common/EntityCampaignHistory';

const ACCOUNTS_ORDER_KEY = 'skylarq:accounts-order';

function loadAccountsOrder(): string[] {
  try {
    const raw = localStorage.getItem(ACCOUNTS_ORDER_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as string[]) : [];
  } catch {
    return [];
  }
}

function saveAccountsOrder(ids: string[]) {
  try {
    localStorage.setItem(ACCOUNTS_ORDER_KEY, JSON.stringify(ids));
  } catch {
    /* localStorage unavailable — order is session-only */
  }
}

// Reorder fetched rows to match the saved order: known entities first (in saved
// order), then any new/unknown entities appended in their server order.
function applySavedOrder(rows: AccountListRow[], savedOrder: string[]): AccountListRow[] {
  if (savedOrder.length === 0) return rows;
  const byId = new Map(rows.map((r) => [r.entity_id, r]));
  const ordered: AccountListRow[] = [];
  const used = new Set<string>();
  for (const id of savedOrder) {
    const row = byId.get(id);
    if (row && !used.has(id)) {
      ordered.push(row);
      used.add(id);
    }
  }
  for (const row of rows) {
    if (!used.has(row.entity_id)) ordered.push(row);
  }
  return ordered;
}

// ─── Top-level: switch between list and detail via local state ───────────────

export function AccountsView({
  seedEntityId,
  onSeedConsumed,
  standupByEntity = {},
}: {
  seedEntityId?: string | null;
  onSeedConsumed?: () => void;
  /** Optional 24h standup data keyed by entity_id — used to enrich list cards. */
  standupByEntity?: Record<string, StandupClientRow>;
} = {}) {
  const [selectedEntityId, setSelectedEntityId] = useState<string | null>(null);
  // Cache the list once at this level so navigating back from a detail doesn't refetch/reload.
  const [rows, setRows] = useState<AccountListRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (seedEntityId) {
      setSelectedEntityId(seedEntityId);
      onSeedConsumed?.();
    }
  }, [seedEntityId, onSeedConsumed]);

  useEffect(() => {
    apiClient
      .getAdminAccounts()
      .then((fetched) => setRows(applySavedOrder(fetched, loadAccountsOrder())))
      .catch((e: { response?: { data?: { detail?: string } } }) =>
        setError(e?.response?.data?.detail || 'Failed to load accounts')
      )
      .finally(() => setLoading(false));
  }, []);

  // Splice rows (remove dragged, insert before target), persist new order.
  const reorderRows = (draggedId: string, targetId: string) => {
    if (draggedId === targetId) return;
    setRows((prev) => {
      const next = [...prev];
      const from = next.findIndex((r) => r.entity_id === draggedId);
      const to = next.findIndex((r) => r.entity_id === targetId);
      if (from === -1 || to === -1) return prev;
      const [moved] = next.splice(from, 1);
      const insertAt = next.findIndex((r) => r.entity_id === targetId);
      next.splice(insertAt, 0, moved);
      saveAccountsOrder(next.map((r) => r.entity_id));
      return next;
    });
  };

  if (selectedEntityId) {
    return (
      <AccountDetailView
        entityId={selectedEntityId}
        onBack={() => setSelectedEntityId(null)}
      />
    );
  }
  return (
    <AccountListView
      rows={rows}
      loading={loading}
      error={error}
      onSelect={setSelectedEntityId}
      onReorder={reorderRows}
      standupByEntity={standupByEntity}
    />
  );
}

// ─── List view ───────────────────────────────────────────────────────────────

function AccountListView({
  rows,
  loading,
  error,
  onSelect,
  onReorder,
  standupByEntity,
}: {
  rows: AccountListRow[];
  loading: boolean;
  error: string | null;
  onSelect: (id: string) => void;
  onReorder: (draggedId: string, targetId: string) => void;
  standupByEntity: Record<string, StandupClientRow>;
}) {
  const [dragId, setDragId] = useState<string | null>(null);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-5 pb-10">
      {loading ? (
        <Spinner size="md" center text="Loading accounts..." />
      ) : error ? (
        <p className="text-sm text-error-600">{error}</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-neutral-500">No accounts with outbound activity yet.</p>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => {
            const sRow = standupByEntity[r.entity_id];
            return (
            <Card
              key={r.entity_id}
              padding="md"
              shadow="sm"
              border
              hoverable
              onClick={() => onSelect(r.entity_id)}
              draggable
              onDragStart={() => setDragId(r.entity_id)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => {
                if (dragId) onReorder(dragId, r.entity_id);
                setDragId(null);
              }}
              onDragEnd={() => setDragId(null)}
              className={`cursor-grab active:cursor-grabbing ${dragId === r.entity_id ? 'opacity-50' : ''}`}
            >
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-semibold text-neutral-900">
                  {r.display_name || r.entity_id}
                </span>
                <div className="flex items-center gap-2">
                  {sRow && sRow.new_reply_count > 0 ? (
                    <span className="text-xs bg-primary-50 text-primary-700 border border-primary-200 rounded px-2 py-0.5">
                      🆕 {sRow.new_reply_count}
                    </span>
                  ) : null}
                  <Badge variant="primary" size="sm">
                    {r.project_count} project{r.project_count === 1 ? '' : 's'}
                  </Badge>
                </div>
              </div>
              {sRow ? (
                <div className="mb-3 border border-neutral-100 bg-neutral-50 rounded px-2 py-1 space-y-0.5">
                  <div className="flex flex-wrap items-center gap-1.5 text-2xs text-neutral-500">
                    <span className="font-medium text-neutral-600">24h email</span>
                    <span>↑{sRow.email_24h.sent ?? 0} sent</span>
                    <span>·</span>
                    <span>{sRow.email_24h.opens ?? 0} op</span>
                    <span>·</span>
                    <span>{sRow.email_24h.replies ?? 0} rep</span>
                    <span>·</span>
                    <span>{sRow.email_24h.meetings ?? 0} mtg</span>
                  </div>
                  {(sRow.linkedin_24h.invited ?? 0) + (sRow.linkedin_24h.accepted ?? 0) + (sRow.linkedin_24h.replied ?? 0) > 0 ? (
                    <div className="flex flex-wrap items-center gap-1.5 text-2xs text-neutral-400">
                      <span className="font-medium">LI:</span>
                      <span>{sRow.linkedin_24h.invited ?? 0} inv</span>
                      <span>·</span>
                      <span>{sRow.linkedin_24h.accepted ?? 0} acc</span>
                      <span>·</span>
                      <span>{sRow.linkedin_24h.replied ?? 0} rep</span>
                    </div>
                  ) : null}
                </div>
              ) : null}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <ChannelFunnel title="Email" channel="email" stats={r.email} />
                <ChannelFunnel title="LinkedIn" channel="linkedin" stats={r.linkedin} />
              </div>
            </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Detail view ─────────────────────────────────────────────────────────────

function AccountDetailView({
  entityId,
  onBack,
}: {
  entityId: string;
  onBack: () => void;
}) {
  const [detail, setDetail] = useState<AccountDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [members, setMembers] = useState<WorkspaceMember[]>([]);

  useEffect(() => {
    setLoading(true);
    setError(null);
    setDetail(null);
    Promise.all([
      apiClient.getAdminAccountDetail(entityId),
      apiClient.getAdminWorkspaceMembers().catch(() => [] as WorkspaceMember[]),
    ])
      .then(([accountDetail, workspaceMembers]) => {
        setDetail(accountDetail);
        setMembers(workspaceMembers);
      })
      .catch((e: { response?: { data?: { detail?: string } } }) =>
        setError(e?.response?.data?.detail || 'Failed to load account')
      )
      .finally(() => setLoading(false));
  }, [entityId]);

  if (loading) {
    return (
      <div className="p-6">
        <Spinner size="md" center text="Loading..." />
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-6">
        <p className="text-sm text-error-600">{error}</p>
      </div>
    );
  }
  if (!detail) return null;

  const un = detail.unassigned || {};
  const hasUnassigned = un.email?.latest || un.linkedin?.latest;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
      <button
        onClick={onBack}
        className="text-sm text-neutral-500 hover:text-neutral-800 mb-4 inline-flex items-center gap-1 transition"
      >
        ← Back to clients
      </button>

      <header className="mb-5">
        <h2 className="text-2xl font-semibold tracking-tight text-neutral-900">
          {detail.display_name || detail.entity_id}
        </h2>
        <p className="text-sm text-neutral-400 mt-0.5">
          Channel performance, campaigns, and account context
        </p>
      </header>

      {detail.projects.length === 0 && !hasUnassigned ? (
        <p className="text-sm text-neutral-500">No channel activity for this account yet.</p>
      ) : null}

      {/* ── Stats first: channels + per-campaign breakdown ──────────────────── */}
      <section className="space-y-4">
        {detail.projects.map((p) => (
          <Card key={p.project_id} padding="md" shadow="sm" border>
            <div className="flex items-center justify-between mb-2">
              <span className="text-base font-semibold text-neutral-900">{p.name}</span>
              {p.pipeline_stage ? (
                <Badge variant="neutral" size="sm">
                  {p.pipeline_stage}
                </Badge>
              ) : null}
            </div>
            <ProjectMetaBar project={p} members={members} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <ChannelFunnel title="Email" channel="email" stats={p.email} />
              <ChannelFunnel title="LinkedIn" channel="linkedin" stats={p.linkedin} />
            </div>
            {p.email_campaigns && p.email_campaigns.length > 0 ? (
              <div className="mt-4">
                <div className="text-2xs font-medium uppercase tracking-wider text-neutral-400 mb-2">
                  Email campaigns
                </div>
                <CampaignStatsTable campaigns={p.email_campaigns} />
              </div>
            ) : null}
            {p.linkedin?.replies && p.linkedin.replies.length > 0 ? (
              <LinkedInRepliesSection replies={p.linkedin.replies} />
            ) : null}
          </Card>
        ))}

        {hasUnassigned ? (
          <Card padding="md" shadow="sm" border>
            <div className="text-2xs font-medium uppercase tracking-wider text-neutral-400 mb-3">
              Client-level / unassigned
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <ChannelFunnel title="Email" channel="email" stats={un.email} />
              <ChannelFunnel title="LinkedIn" channel="linkedin" stats={un.linkedin} />
            </div>
            {un.linkedin?.replies && un.linkedin.replies.length > 0 ? (
              <LinkedInRepliesSection replies={un.linkedin.replies} />
            ) : null}
          </Card>
        ) : null}
      </section>

      {/* ── Then context: brief + activity ──────────────────────────────────── */}
      <section className="mt-6 space-y-4">
        <ClientBriefPanel entityId={entityId} />
        <EntityCampaignHistory entityId={entityId} />
        <ClientTimeline entityId={entityId} />
      </section>
    </div>
  );
}

// ─── LinkedIn replies sub-section ────────────────────────────────────────────

function LinkedInRepliesSection({ replies }: { replies: LinkedInReply[] }) {
  return (
    <div className="mt-4 pt-4 border-t border-neutral-100">
      <div className="text-13 font-semibold text-neutral-700 mb-3">LinkedIn Replies</div>
      <div className="space-y-2">
        {replies.map((r, i) => (
          <ReplyRow key={r.reply_key ?? i} reply={r} />
        ))}
      </div>
    </div>
  );
}
